import { useCallback, useState } from "react";
import { Option } from "../../models/generics";
import { Google } from "../../api/Google";
import { useToasts } from "../../contexts/toasts";
import { Select, Spin } from "antd";

interface City {
  name: string;
  lat: number;
  lng: number;
  postalCode: string;
  locality: string;
}

type Props = {
  onSelectCity: (city: City) => void;
  disabled: boolean;
  value?: string;
  city?: boolean;
  placeholder: string;
};

export default function GooglePlacesSelect({
  onSelectCity,
  disabled,
  value,
  city,
  placeholder
}: Props) {
  const { showToast } = useToasts();
  const [options, setOptions] = useState<Option[]>();

  const getComponent = (type: string, addressComponents: any[]) =>
    addressComponents.find((component: any) => component.types.includes(type))
      ?.long_name;

  const fetchCities = useCallback(
    (query: string) => {
      if (!query) return;

      setOptions(undefined);

      Google.fetchCities({
        input: query,
        types: city ? "(cities)" : "address",
      }).then(
        ({ data }) => {
          if (data.predictions) {
            setOptions(
              data.predictions.map(
                (place: { description: string; place_id: string }) => ({
                  label: place.description,
                  value: place.place_id,
                })
              )
            );
          }
        },
        () => {
          setOptions([]);
          showToast(
            "places-error",
            "Errore nel recupero dei dati della città",
            "",
            "danger"
          );
        }
      );
    },
    [showToast, city]
  );

  const handleSelect = useCallback(
    (placeId: string) => {
      Google.getPlaceDetails({
        place_id: placeId,
      }).then(
        ({ data }) => {
          if (data.result && data.result.geometry) {
            const { lat, lng } = data.result.geometry.location;
            const postalCode = getComponent(
              "postal_code",
              data.result.address_components
            );
            const locality = getComponent(
              "locality",
              data.result.address_components
            );
            const cityName = data.result.name;
            onSelectCity({ name: cityName, lat, lng, postalCode, locality });
          }
        },
        () => {
          setOptions([]);
          showToast(
            "place-error",
            "Errore nel recupero dei dati della città",
            "",
            "danger"
          );
        }
      );
    },
    [onSelectCity, showToast]
  );

  return (
    <Select
      disabled={disabled}
      showSearch
      value={value}
      filterOption={false}
      onSearch={fetchCities}
      onSelect={handleSelect}
      options={options}
      notFoundContent={!options ? <Spin size="small" /> : "Nessun risultato"}
      placeholder={placeholder}
      style={{ width: "100%" }}
    />
  );
}
